import React, { useState, useEffect } from 'react'

import { useNavigate, useParams, } from "react-router-dom";

// axios
import axios from 'axios';
import { apiClient } from 'api-client';

import { addDays, parseISO, format } from 'date-fns';

// language
import { injectIntl, IntlShape } from 'react-intl';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setRefresh } from 'redux/actions/pageActions';
import { setContainer, setContainerReservations } from 'redux/actions/containersActions';
import { setItems } from 'redux/actions/inventoryActions';

// alerts
import { useSnackbar } from "notistack";

// Functions
import { handleDeleteFilters } from 'context/tableContext';

// items table
import ItemsTable from 'pages/containers/ContainerDetail/itemsTable';

// components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import LoadingComponent from 'components/LoadingComponent';

// components-lib
import ContainerInfo from "components-lib/ContainerInfo";
import ReservationInContainer from "components-lib/ReservationInContainer";
import Legend from 'components-lib/Legend';

// extracted data
import { extractDataContainer, extractDataItems } from 'common/extractData';

// types
import { ContainerType, ItemsInContainerType } from 'types/types';
import {
  ItemsContainerData,
  ContainerData,
  FilterState,
  Refresh,
  ContainerReservationsData,
} from 'types/typesRedux';

// css
import '../../../css/mediaQuery.css'


type Props = {
  intl: IntlShape,
}

const ContainerDetail = ( { intl, }: Props ) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();

  // redux hooks
  const items = useSelector( (state: ItemsContainerData) => state.itemsInContainer );
  const container = useSelector( (state: ContainerData) => state.container.container );
  const containerReservations = useSelector( (state: ContainerReservationsData) => state.containerReservations.containerReservations );
  const filters = useSelector( (state: FilterState) => state.filters);
  const refresh = useSelector( (state: Refresh) => state.handleRefresh);

  // If storedRowLimit is not null and is a valid number, use it as rowLimit; otherwise, use the default value.
  const storedRowLimit = parseInt(localStorage.getItem('rowLimitItemsInContainer'));
  const initialRowLimit = storedRowLimit && !isNaN(storedRowLimit) ? storedRowLimit : items.limit;

  const [ containerId, setContainerId ] = useState<number>(null);
  const [ warningTime, setWarningTime ] = useState(null);
  const tableItemsName = intl.formatMessage( {id: 'tableItems.title'} );
  const [ localLoading, setLocalLoading ] = useState<{ container: boolean; items: boolean; reservations: boolean }>({
    container: true,
    items: true, 
    reservations: true,
    }
  );
 
  // /*
  // */ Handle container
  //

  // Get container
  const getContainer = async ( id: number ) => {
    const cancelToken = axios.CancelToken.source()
    try{
      const response = await apiClient.get(`/v1/containers/${id}`,{
        cancelToken:cancelToken.token
      });

      if (response) {
        const responseData = extractDataContainer(intl)(response.data);
        dispatch(setContainer( responseData as ContainerType ));
        // console.log(response)
        setLocalLoading(prevLoading => ({
          ...prevLoading,
          container: false,
        }));
        // console.log(response.data)
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled")
      } else {
        // todo:handle error
      }
    }
    handleRefreshPage();
    return () => cancelToken.cancel();
  }

  const transformSort = (sort: string) => {
    if ( sort.includes('name') ) {
      // Split the string using ":" as the delimiter
      let parts = sort.split(":");
      // parts will be an array with two elements: ["name", "asc"]
      let propertyName = parts[0];
      let sortOrder = parts[1];
      return `category[${propertyName}]:${sortOrder}`;
    } else { 
      return sort }
  }

  // get warning time opened door
  const getSettings = async () => {
    const cancelToken = axios.CancelToken.source()
    
    try{
      const response = await apiClient.get("/v1/settings",{
        cancelToken:cancelToken.token
      });

      if (response) {
        let responseTime = Number(response.data.doorOpenWarningAfter) / 60;
        // console.log(responseTime)
        setWarningTime(responseTime);
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    }
    return () => cancelToken.cancel()   
  };

  // Get Items in container
  const getItems = async ( id: number, params = {} ) => {
    const cancelToken = axios.CancelToken.source()
    const sortType = ( filters[`${tableItemsName} sort`] && 
    filters[`${tableItemsName} sort`].sort !== undefined ) 
    ? filters[`${tableItemsName} sort`].sort 
    : items.sort;

    const defaultParams = {
      offset: items ? items.offset : 0,
      limit: initialRowLimit,
      sort: transformSort( sortType )
    };

    // Merge provided params with default params
    const mergedParams = { ...defaultParams, ...params };

    try{
      const response = await apiClient.get(`/v1/containers/${id}/items`,{
        params: mergedParams,
        cancelToken:cancelToken.token
      });

      if (response) {
        const responseData = response.data.items.map((item: unknown) => extractDataItems(intl)(item as ItemsInContainerType));
        const totalCount = response.data.totalCount;
        dispatch(setItems( responseData, totalCount ));
        setLocalLoading(prevLoading => ({
          ...prevLoading,
          items: false,
        }));
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    } 
    handleRefreshPage();
    return () => cancelToken.cancel();
  }

  
  const handleNextPage = () => {
    const lastObject = containerReservations[containerReservations.length - 1];
    const lastDateString = lastObject.date;
    const parsedDate = parseISO(lastDateString);
    const nextDate = addDays(parsedDate, 1);
    const nextDateString = format(nextDate, "yyyy-MM-dd");
    const nextDateEnd = addDays(parsedDate, 21);
    const nextDateStringEnd = format(nextDateEnd, "yyyy-MM-dd");

    const nextPage = {
      from: nextDateString,
      to: nextDateStringEnd,
    };

    getReservations(containerId, nextPage);
  };

  const handlePreviousPage = () => {
    const firstObject = containerReservations[0];
  const firstDateString = firstObject.date; // napr. "2025-03-06"
  const parsedDate = parseISO(firstDateString);
  
  // Odpočítaj 1 deň, aby horná hranica bola deň pred prvým dátumom
  const previousDate = addDays(parsedDate, -1);
  const previousDateString = format(previousDate, "yyyy-MM-dd");

  // Odpočítaj 21 dní od prvého dátumu ako začiatok predchádzajúcej stránky
  const previousPageStart = addDays(parsedDate, -21);
  const previousPageStartString = format(previousPageStart, "yyyy-MM-dd");

  const previousPage = {
    from: previousPageStartString,
    to: previousDateString,
  };

  // Zavolaj API pre získanie rezervácií pre predchádzajúcu stránku
  getReservations(containerId, previousPage);
  }

  // Get Items in container
  const getReservations = async ( id: number, params = {} ) => {
    const cancelToken = axios.CancelToken.source()

    try{
      const response = await apiClient.get(`/v1/reservations/calendar/by-container/${id}`,{
        params: params,
        cancelToken:cancelToken.token
      });

      if (response) {
        const totalCount = response.data.totalCount;
        dispatch(setContainerReservations( response.data, totalCount ));
        setLocalLoading(prevLoading => ({
          ...prevLoading,
          reservations: false,
        }));
        // console.log(response)
      } else {
        console.log("No response received.");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    } 
    // handleRefreshPage();
    return () => cancelToken.cancel();
  }

  // Handle enabled status in API
  const handleContainerStatus = async ( id: number, enabled: true | false ) => {
    const cancelToken = axios.CancelToken.source()
    
    try {
      const response = await apiClient.patch(`/v1/containers/${id}`,{
        isEnabled: enabled,
        cancelToken:cancelToken.token
      });
      if (response) {
        // console.log( response.data )
        dispatch( setRefresh( { refreshContainer: true } ));
        // setStatusLoading( false )
        enqueueSnackbar(`${intl.formatMessage({id:'containerAlert.editEnabled.true'})}`, { variant: 'success' });        
      }
    } catch ( error ){
      // setStatusLoading( false )
      console.log( 'err' );
      enqueueSnackbar(`${intl.formatMessage({id:'containerAlert.editEnabled.false'})}`, { variant: 'success' });        
    }
    return () => cancelToken.cancel()
  };

  // If refresh is true set them to false
  const handleRefreshPage = () => {
    if ( refresh.refresh === true ) { dispatch(setRefresh( { refresh: false } )) };
    if ( refresh.refreshContainer === true ) { dispatch(setRefresh( { refreshContainer: false } )) };
    if ( refresh.refreshInventory === true ) { dispatch(setRefresh( { refreshInventory: false } )) };
    if ( refresh.refreshLanguage === true ) { dispatch(setRefresh( { refreshLanguage: false } )) };
  }

  // if have id get data
  useEffect(() => {
    let Id = parseInt(id)
    if (Id) {
      setContainerId( Id );
      getContainer( Id );
      getItems( Id );
      getReservations( Id );
      getSettings();
    }
  },[id]);


  useEffect(() => {
    if ( refresh.refresh === true ) {
      getItems( containerId, filters[tableItemsName] );
    }
  }, [ refresh.refresh ])

  // Refresh when change language
  useEffect(() => {
    if ( refresh.refreshLanguage === true ) {
      getItems( containerId, filters[tableItemsName] );
      getContainer( containerId );
    }
  },[ refresh.refreshLanguage ] )
  
  useEffect(() => {
    if ( refresh.refreshContainer === true ) {
      getContainer( containerId );
    }
  }, [ refresh.refreshContainer ])

  useEffect(() => {
    if ( refresh.refreshInventory === true ) {
      getItems( containerId );
    }
  }, [ refresh.refreshInventory ])

  // Handle enabled status
  const handleContainerEnabled = () => {
    let isEnabled = container.isEnabled === intl.formatMessage({id:'tableContainers.isEnabledTrue'})
    ? false : true;
    if ( container.isEnabled ){
      // setStatusLoading( true )
      handleContainerStatus( container.id, isEnabled );
      // setContainerStatus(null);
    }
  };

  const handleBtnContainerEnabledText = () => {
    if ( container.isEnabled === intl.formatMessage({id:'tableContainers.isEnabledTrue'})){
      return intl.formatMessage({id:'button.containerOutOfOrder'});
    } else {
      return intl.formatMessage({id:'button.containerActivate'});
    }
  }

  // go to containers table
  const handleBtnBack = () => {
    handleDeleteFilters( dispatch, tableItemsName)
  }

  return (
    <>
      <MDBox
        sx={{ marginLeft: "max(18.7rem)", paddingRight: "1rem", width: "auto",
        display: "flex", flexDirection: "column", justifyContent: "center", marginTop: "1rem",
        maxWidth: "88rem",
        }}
      >
        <MDBox
          sx={{
            display: "flex", justifyContent: "space-between",
            width: "100%", pb: "2.5rem",            
          }} 
        >
          <MDButton
            variant="outlined"
            color="success"
            onClick={() => { handleBtnBack(); navigate(-1) }}
          >
            {intl.formatMessage({id:'button.back'})}
          </MDButton>
          
          <MDButton
            variant="contained"
            color="success"
            onClick={() => { handleContainerEnabled() }}
          >
            {handleBtnContainerEnabledText()}
          </MDButton>
        </MDBox>

        <MDBox 
          className= "containerContainer"
          sx={{
            width:"100%", gap:"1rem" 
          }}
        >

          { ( localLoading.container || localLoading.reservations ) ? (
          <LoadingComponent /> ) : (

            <MDBox
              sx={{ 
                minWidth: "45rem", width: "auto",
                maxWidth: "45rem", pb: "1rem",
              }}
            >
              <ContainerInfo />
              
              <MDBox
                sx={{
                  marginTop: "2.5rem",
                }}
              >
                <ReservationInContainer
                  handleNextPage={handleNextPage}
                  handlePreviousPage={handlePreviousPage}
                />
              </MDBox>
            </MDBox>
            )} 

          { ( localLoading.items ) ? (
          <LoadingComponent /> ) : (

            <MDBox
              className= "containerContainerItems"
              sx={{       
                minWidth: "34rem", width: "auto",
                maxWidth: "44rem", pb: "1rem",
              }}
            >
              <ItemsTable 
                initialRowLimit={initialRowLimit}
              />

              <Legend
                type={'containerDetail'}
                warningTime={warningTime}
              />

            </MDBox>
          )} 

        </MDBox>      

      </MDBox>
    </>
  )
}

export default injectIntl(ContainerDetail);